import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';
import type { DeviceRoute } from './useDeviceRoutes';

export interface DeviceRouteChangeEvent {
  timestampMs: number;
  kind: 'snapshot' | 'delta';
  baseTimestampMs: number | null;
  adds: DeviceRoute[];
  removes: DeviceRoute[];
  state: DeviceRoute[];
}

export interface DeviceRoutesHistoryResponse {
  events: DeviceRouteChangeEvent[];
  count: number;
  oldestTimestampMs: number | null;
  hasMore: boolean;
}

interface RawRoute {
  destination_subnet: string;
  next_hop_ip: string | null;
  interface_name: string | null;
  interface_ifindex: string | null;
  route_type: string | null;
  route_protocol: string | null;
  metric: number | null;
  vrf?: string | null;
}

interface RawEvent {
  timestamp_ms: number;
  kind: string;
  base_timestamp_ms: number | null;
  adds: RawRoute[];
  removes: RawRoute[];
  state: RawRoute[];
}

interface RawResponse {
  events: RawEvent[];
  count: number;
  oldest_timestamp_ms: number | null;
  has_more: boolean;
}

// Backend uses snake_case; frontend uses camelCase. Convert at the boundary.
function toCamelRoute(r: RawRoute): DeviceRoute {
  return {
    destinationSubnet: r.destination_subnet,
    nextHopIp: r.next_hop_ip,
    interfaceName: r.interface_name,
    interfaceIfindex: r.interface_ifindex,
    routeType: r.route_type,
    routeProtocol: r.route_protocol,
    metric: r.metric,
    vrf: r.vrf ?? null,
  };
}

function toCamel(raw: RawResponse): DeviceRoutesHistoryResponse {
  return {
    events: raw.events.map((e) => ({
      timestampMs: e.timestamp_ms,
      // Backend has its own enum (snapshot/delta/heartbeat) but the
      // history endpoint only returns the first two. Narrow defensively.
      kind: e.kind === 'snapshot' ? 'snapshot' : 'delta',
      baseTimestampMs: e.base_timestamp_ms,
      adds: e.adds.map(toCamelRoute),
      removes: e.removes.map(toCamelRoute),
      state: e.state.map(toCamelRoute),
    })),
    count: raw.count,
    oldestTimestampMs: raw.oldest_timestamp_ms,
    hasMore: raw.has_more,
  };
}

/**
 * Fetch the routing-table change history for a device.
 *
 * Two modes — pick one:
 * - **Cursor mode** (default): pass `endMs` + `changes`. Returns the most
 *   recent N events at or before `endMs`, sorted newest-first. Use
 *   `response.oldestTimestampMs` as the next `endMs` to paginate older.
 * - **Range mode**: pass `startMs` + `endMs` (leave `changes` undefined).
 *   Returns every event in `[start, end)` sorted oldest-first — natural
 *   for left-to-right sliders and timeline plots.
 *
 * Backend caps the response at 500 events regardless of `changes`.
 */
export function useDeviceRoutesHistory(
  deviceId: string | undefined,
  opts: { startMs?: number | null; endMs?: number | null; changes?: number | null } = {}
) {
  const contextReady = useActiveContextReady();
  const { startMs, endMs, changes } = opts;
  return useQuery<DeviceRoutesHistoryResponse>({
    queryKey: ['deviceRoutesHistory', deviceId, startMs ?? null, endMs ?? null, changes ?? null],
    queryFn: async () => {
      if (!deviceId) throw new Error('Device ID is required');
      const params: Record<string, number> = {};
      if (startMs != null) params.start = startMs;
      if (endMs != null) params.end = endMs;
      if (changes != null) params.changes = changes;
      const response = await axios.get<RawResponse>(
        `${config.backendApi}/api/devices/${deviceId}/routes/history`,
        { withCredentials: true, params }
      );
      return toCamel(response.data);
    },
    enabled: !!deviceId && contextReady,
  });
}
